import { useMemo, useRef, useState } from 'react';
import { Popover } from '@base-ui/react/popover';
import { DayPicker, type DateRange, type DayProps } from 'react-day-picker';
import 'react-day-picker/style.css';
import { TriggerContent } from '../DatePicker/TriggerContent';
import styles from './DateRangeField.module.css';

export interface IDateRange {
  from: Date | null;
  to: Date | null;
}

interface IDateRangeFieldProps {
  value: IDateRange;
  onChange: (next: IDateRange) => void;
  placeholder: string;
  ariaLabel: string;
  minDate?: Date;
  maxDate?: Date;
  /** Specific calendar days to disable within the visible range. */
  disabledDates?: Date[];
  disabled?: boolean;
}

// Tz-naive by convention, like `DateTimeField`: the value's *local* calendar
// fields are the canonical picked date, and the submit boundary re-anchors them
// to the project tz (`pickerDateToProjectNoonMs` in @utils/dateUtils). A
// `timeZone` here would re-read the epoch through a fixed zone and disagree with
// the field's own selection logic for any browser outside it.
const formatter = new Intl.DateTimeFormat('ja-JP', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const startOfDay = (d: Date): number => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c.getTime();
};

export function DateRangeField({ value, onChange, placeholder, ariaLabel, minDate, maxDate, disabledDates, disabled }: IDateRangeFieldProps) {
  const [open, setOpen] = useState(false);
  // Anchor of the in-progress range — the day that's being hovered after
  // the user has picked a start but not yet a finish. Drives the live
  // preview band so the range becomes visible as the cursor moves.
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  // Custom DayPicker `Day` component. Wires our own mouseEnter handler
  // (rdp v9's `onDayMouseEnter` doesn't fire when a custom `Day` is
  // supplied) AND keeps the jsdom-friendly click-forwarding behaviour
  // we need for tests.
  const HoverDay = useMemo(() => {
    return function HoverDay({ day, modifiers: _modifiers, ...tdProps }: DayProps) {
      const ref = useRef<HTMLTableCellElement>(null);
      return (
        <td
          ref={ref}
          {...tdProps}
          onMouseEnter={(e) => {
            tdProps.onMouseEnter?.(e);
            setHoverDate(day.date);
          }}
          onClick={(e) => {
            tdProps.onClick?.(e);
            if (!e.defaultPrevented) {
              const btn = ref.current?.querySelector<HTMLButtonElement>('button');
              if (btn && e.target !== btn) btn.click();
            }
          }}
        />
      );
    };
  }, []);

  const display = (() => {
    if (!value.from && !value.to) return null;
    if (value.from && !value.to) return formatter.format(value.from) + ' →';
    if (!value.from && value.to) return '→ ' + formatter.format(value.to);
    return `${formatter.format(value.from!)} → ${formatter.format(value.to!)}`;
  })();

  // Preview is active when the user has anchored a start but hasn't
  // committed a different end yet. react-day-picker emits
  // `{ from: D, to: D }` after the first click of a fresh range, so
  // we treat `from === to` the same as "no end yet".
  const isPickingEnd =
    !!value.from &&
    (!value.to || startOfDay(value.from) === startOfDay(value.to));
  const previewActive = isPickingEnd && !!hoverDate;
  let previewModifiers: Record<string, (d: Date) => boolean> = {};
  if (previewActive && value.from && hoverDate) {
    const fromTs = startOfDay(value.from);
    const hoverTs = startOfDay(hoverDate);
    if (hoverTs !== fromTs) {
      const lo = Math.min(fromTs, hoverTs);
      const hi = Math.max(fromTs, hoverTs);
      const forward = hoverTs > fromTs;
      previewModifiers = {
        // Strictly between the two endpoints — gets the full-cell band.
        previewMiddle: (d: Date) => {
          const t = startOfDay(d);
          return t > lo && t < hi;
        },
        // The hovered cell — navy circle plus a half-band that points
        // back toward the anchored `from`. Direction matters so the
        // band visually connects, not floats.
        previewEndForward: (d: Date) =>
          forward && startOfDay(d) === hoverTs,
        previewEndBackward: (d: Date) =>
          !forward && startOfDay(d) === hoverTs,
      };
    }
  }

  return (
    <Popover.Root open={open} onOpenChange={(o) => { setOpen(o); if (!o) setHoverDate(null); }}>
      <Popover.Trigger type="button" aria-label={ariaLabel} className={styles.trigger} disabled={disabled}>
        <TriggerContent display={display} placeholder={placeholder} />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner className={styles.positioner} sideOffset={4} align="start" collisionPadding={8}>
          <Popover.Popup className={styles.popup}>
            <DayPicker
              mode="range"
              selected={{
                from: value.from ?? undefined,
                to: value.to ?? undefined,
              }}
              onSelect={(range: DateRange | undefined) => {
                onChange({ from: range?.from ?? null, to: range?.to ?? null });
                if (
                  range?.from &&
                  range?.to &&
                  range.from.getTime() !== range.to.getTime()
                ) {
                  setHoverDate(null);
                  setOpen(false);
                }
              }}
              modifiers={previewModifiers}
              modifiersClassNames={{
                previewMiddle: 'rdp-previewMiddle',
                previewEndForward: 'rdp-previewEndForward',
                previewEndBackward: 'rdp-previewEndBackward',
              }}
              disabled={[
                ...(minDate ? [{ before: minDate }] : []),
                ...(maxDate ? [{ after: maxDate }] : []),
                ...(disabledDates ?? []),
              ]}
              numberOfMonths={2}
              components={{ Day: HoverDay }}
            />
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
